import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppPath } from '@app/app-path';
import { AgencyClientsRecord } from '@core/models/agency-clients-record';
import { Customer } from '@core/models/customer.model';
import { UserProfile } from '@core/models/user-profile';
import { Logger } from '@core/services/logger.service';
import { UserProfilesService } from '@core/services/user-profiles.service';
import { OrderCreationCustomerRole } from '@shared/enums/order-creation-customer-role';
import { ChooseAdvertiserNavigationState } from '@shared/models/choose-advertiser-navigation-state';
import { OrderCreationNavigationState } from '@shared/models/order-creation-navigation-state';
import { combineLatest, filter, map, Observable, Subscription, take } from 'rxjs';
import {AuthService} from '@core/services/auth.service';
import {ExternalPlannerLoginResponse} from '@core/models/external-planner-login-response.model';

enum Scenario {
	NotInitialized,
	Scenario1_singleAgency,
	Scenario2_singleAdvertiser,
	Scenario3_multiAgencies,
	Scenario4_multiAdvertisers,
	Scenario5_externalPlanner
}

@Injectable({
	providedIn: 'root'
})
export class OrderCreatorService {

	private readonly COMPONENT_NAME = "OrderCreatorService";

	private profile: UserProfile | undefined;
	private agencyClientsRecords: AgencyClientsRecord[] = [];
	private externalPlanInfo: ExternalPlannerLoginResponse | undefined

	private _scenario: Scenario = Scenario.NotInitialized;
	private _agencyId: string | undefined;

	constructor (
		private _router: Router,
		private _userProfilesService: UserProfilesService,
		private _logger: Logger,
		private _authService: AuthService
	) { }

	/**
	 * The method retrieves the user profile and agency-client data for each customer in the profile.
	 * Must be called before any other method is used.
	 * @returns observable, indicating the init has been completed
	 */
	init(): Observable<void> {
		this.resetState();
		// const profile$ = this._userProfilesService.userProfile$;

		const profile$ = this._userProfilesService.userProfile$.pipe(
			filter((userProfile: UserProfile | undefined) => Boolean(userProfile)),
			take(1));

		const agencyClientsRecords$ = this._userProfilesService.getUserProfileClients();
		const externalPlanInfo = this._authService.getExternalPlannerInfo

		return combineLatest([profile$, agencyClientsRecords$]).pipe(
			map(([profile, agencyClientsRecords]) => {
				this.initState(profile, agencyClientsRecords, externalPlanInfo);
				return undefined;
			})
		);
	}

	private initState(profile: UserProfile | undefined, agencyClientsRecords: AgencyClientsRecord[] | undefined, externalPlanInfo: ExternalPlannerLoginResponse | undefined) {
		if (!profile) {
			this._logger.error(this.COMPONENT_NAME, "Error in the initState(): the profile is empty");
			return;
		}

		if (!agencyClientsRecords || agencyClientsRecords.length === 0) {
			this._logger.error(this.COMPONENT_NAME, "Error in the initState(): the agencyClientsRecords array is empty");
			return;
		}

		this.profile = profile;
		this.agencyClientsRecords = agencyClientsRecords;
		this.externalPlanInfo = externalPlanInfo

		if (externalPlanInfo && externalPlanInfo.planId && externalPlanInfo.advertiserId && externalPlanInfo.agencyId) {
			// It is being checked first because if it is true it should not bother other cases
			// Scenario 5: User is redirected from External Planner System and will create order directly using the provided details with External PlanId
			this._scenario = Scenario.Scenario5_externalPlanner
		}
		else if (agencyClientsRecords.length === 1 && agencyClientsRecords[0].clients && agencyClientsRecords[0].clients.length > 0) {
			// Scenario 1: profile.customers.length = 1 and GET / cass / clients / clients returns one or more clients(meaning it is an agency).
			this._scenario = Scenario.Scenario1_singleAgency;
		}
		else if (agencyClientsRecords.length === 1 && (!agencyClientsRecords[0].clients || agencyClientsRecords[0].clients.length === 0)) {
			// Scenario 2: profile.customers.length = 1 and GET / cass / clients / clients returns NO clients(meaning it is NOT an agency).
			this._scenario = Scenario.Scenario2_singleAdvertiser;
		}
		else if (agencyClientsRecords.length > 0 && agencyClientsRecords.every((agencyClientsRecord: AgencyClientsRecord) => agencyClientsRecord.clients && agencyClientsRecord.clients.length > 0)) {
			// Scenario 3: profile.customers.length > 1
			// 1. If * all * of the customers are agencies(GET / cass / clients / clients returns something for each of them):
			this._scenario = Scenario.Scenario3_multiAgencies;
		}
		else {
			this._scenario = Scenario.Scenario4_multiAdvertisers;
		}
	}

	private resetState(): void {
		this._scenario = Scenario.NotInitialized;
		this._agencyId = undefined;
	}

	startOrderCreationFlow(): void {
		switch (this._scenario) {
			case Scenario.Scenario1_singleAgency:
				//	1.1. Show advertisers list from the clients of customers[0].
				const agencyId = this.profile?.customers?.[0]?.id;
				const agencyClients = this.getClientsByAgencyId(agencyId);
				this.goSelectCustomer(agencyClients, OrderCreationCustomerRole.Client);
				break;

			case Scenario.Scenario2_singleAdvertiser:
				// 2.2. Create the order with order.otherPayerId set to undefined and order.advertiserId set to customers[0].
				const customer = this.profile?.customers?.[0];
				this.goCreateOrder(customer?.id, customer?.name, undefined);
				break;

			case Scenario.Scenario3_multiAgencies:
				// 3.1.1. Show a new page Select Agency allowing the user to choose an agency.
				this.goSelectCustomer(this.profile?.customers, OrderCreationCustomerRole.Agency);
				break;

			case Scenario.Scenario4_multiAdvertisers:
				// 3.2.1. Show the existing choose advertiser page listing the profile customers allowing the user to choose a customer (which may or may not be an agency). (Note: the page is still titled "Choose advertiser".)
				this.goSelectCustomer(this.profile?.customers, OrderCreationCustomerRole.Customer);
				break;

			case Scenario.Scenario5_externalPlanner:
				const clientAdvertisers = this.getClientsByAgencyId(this.externalPlanInfo?.agencyId)
				const advertiser = clientAdvertisers?.find((x) => x.id === this.externalPlanInfo?.advertiserId)
				this.goCreateOrder(this.externalPlanInfo?.advertiserId, advertiser?.name, this.externalPlanInfo?.agencyId);
				break;

			default:
				this._logger.error(this.COMPONENT_NAME, `Error in the startOrderCreationFlow(): the scenario="${this._scenario}" is not supported`);
				break;
		}
	}

	private goSelectCustomer(customers: Customer[] | undefined, role: OrderCreationCustomerRole): void {
		if (!customers || customers.length === 0) {
			this._logger.error(this.COMPONENT_NAME, `Error in the goSelectCustomer(): the customers arg is empty, scenario="${this._scenario}", role="${role}", profileUserId="${this._userProfilesService.userProfile$.value?.id}"`);
			return;
		}

		let titleKey: string | undefined;
		let queryParamValue: string | undefined;
		switch (role) {
			case OrderCreationCustomerRole.Customer:
				titleKey = "CHOOSE_ADVERTISER_PAGE.MAIN_TITLE"
				queryParamValue = "customer";
				break;
			case OrderCreationCustomerRole.Agency:
				titleKey = "CHOOSE_ADVERTISER_PAGE.CHOOSE_AGENCY";
				queryParamValue = "agency";
				break;
			case OrderCreationCustomerRole.Client:
				titleKey = "CHOOSE_ADVERTISER_PAGE.MAIN_TITLE"
				queryParamValue = "client";
				break;
			default:
				this._logger.error(this.COMPONENT_NAME, `Error in the goSelectCustomer(): the role="${role}" is not supported, scenario="${this._scenario}", profileUserId="${this._userProfilesService.userProfile$.value?.id}"`);
				break;
		}

		const state = new ChooseAdvertiserNavigationState();
		state.titleKey = titleKey;
		state.customers = customers;
		state.role = role;

		this._router.navigate([AppPath.ChooseAdvertiser], {
			queryParams: { role: queryParamValue }, // this dummy parameter forces the browser to add this path to the history even if the component is navigating to itself
			state: state
		});
	}

	private goCreateOrder(advertiserId: string | undefined, advertiserName: string | undefined, otherPayerId: string | undefined) {
		if (!advertiserId) {
			this._logger.error(this.COMPONENT_NAME, `Error in the goCreateOrder(): the advertiserId is empty, scenario="${this._scenario}", profileUserId="${this._userProfilesService.userProfile$.value?.id}"`);
			return;
		}
		if (!advertiserName) {
			this._logger.warning(this.COMPONENT_NAME, `Invalid data detected in the goCreateOrder(): the advertiserName is empty for advertiserId="${advertiserId}", scenario="${this._scenario}", profileUserId="${this._userProfilesService.userProfile$.value?.id}"`);
		}

		const state = new OrderCreationNavigationState();
		state.orderOwner.advertiserId = advertiserId;
		state.orderOwner.advertiserName = advertiserName;
		state.orderOwner.otherPayerId = otherPayerId;
		state.orderOwner.otherPayerName = this.getProfileCustomerNameById(otherPayerId)

		this._router.navigate([AppPath.Order], {
			state: state
		});
	}

	actOnCustomerSelected(customer: Customer, role: OrderCreationCustomerRole | undefined): void {
		switch (this._scenario) {
			case Scenario.Scenario1_singleAgency:
				// 1.2. Create the order with order.otherPayerId set to profile.customers[0] and order.advertiserId set to the client selected in the advertisers list in step 1.1.
				const otherPayerId = this.profile?.customers?.[0]?.id;
				this.goCreateOrder(customer.id, customer.name, otherPayerId);
				break;
			case Scenario.Scenario2_singleAdvertiser:
				this._logger.error(this.COMPONENT_NAME, `Bug: the actOnCustomerSelected is called when must not for scenario="${this._scenario}", role="${role}", profileUserId="${this._userProfilesService.userProfile$.value?.id}"`);
				break;
			case Scenario.Scenario3_multiAgencies:
				if (role === OrderCreationCustomerRole.Agency) {
					//  1.1.Show a new page Select Agency allowing the user to choose an agency.
					//	1.2.Show the existing choose advertiser page listing clients of the selected agency.
					this._agencyId = customer.id;
					const clients = this.getClientsByAgencyId(customer.id);
					this.goSelectCustomer(clients, OrderCreationCustomerRole.Client);
				}
				else if (role === OrderCreationCustomerRole.Client) {
					//	1.3.Create the order with order.otherPayerId set to the selected agency and order.advertiserId set to the selected client.
					this.goCreateOrder(customer.id, customer.name, this._agencyId);
				}
				break;
			case Scenario.Scenario4_multiAdvertisers:
				if (role === OrderCreationCustomerRole.Customer) {
					const clients = this.getClientsByAgencyId(customer.id);
					if (clients && clients.length > 0) {
						// 3.2.2. If the chosen customer is an agency:
						// 3.2.2.1.Show the existing choose advertiser page listing clients of the selected agency allowing the user to choose a client.
						this._agencyId = customer.id;
						this.goSelectCustomer(clients, OrderCreationCustomerRole.Client);
					}
					else {
						// 3.2.2.3. If the chosen customer is NOT an agency:
						// 3.2.2.3.1.Create the order with order.otherPayerId set to undefined and order.advertiserId set to the chosen customer.
						this.goCreateOrder(customer.id, customer.name, undefined);
					}
				}
				else if (role === OrderCreationCustomerRole.Client) {
					// 3.2.2.2. Create the order with order.otherPayerId set to the chosen agency and order.advertiserId set to the chosen client.
					this.goCreateOrder(customer.id, customer.name, this._agencyId);
				}
				break;
			default:
				this._logger.error(this.COMPONENT_NAME, `actOnCustomerSelected(): scenario="${this._scenario}" is not supported, role="${role}", profileUserId="${this._userProfilesService.userProfile$.value?.id}"`);
				break;
		}
	}

	private getClientsByAgencyId(agencyId: string | undefined): Customer[] | undefined {
		// The agencyId parameter is defined with "| undefined" type to move the error checking and error reporting from complicated business logic to a single place (here).

		if (!agencyId) {
			this._logger.error(this.COMPONENT_NAME, `getClientsByAgencyId(): the agencyId is empty, scenario="${this._scenario}", profileUserId="${this._userProfilesService.userProfile$.value?.id}"`);
			return undefined;
		}

		if (this.agencyClientsRecords) {
			const record = this.agencyClientsRecords.find((agencyClientsRecord: AgencyClientsRecord) => agencyClientsRecord.agencyId === agencyId);
			if (record) {
				return record.clients;
			}
			else {
				this._logger.error(this.COMPONENT_NAME, `getClientsByAgencyId(): cannot find a record in the agencyClientsRecords for the agencyId="${agencyId}", scenario="${this._scenario}", profileUserId="${this._userProfilesService.userProfile$.value?.id}"`);
			}
		}
		return undefined;
	}

	/**
	 * Handles the case when the order creation is initiated from the advertiser card.
	 * Sets the advertiserId to the selected advertiser and the otherPayerId to the profile customer to which the advertiser belongs if the advertiser is a client.
	 * It is not a part of the other multi-steps order creation flow but still requires to run init() prior.
	 * @param id
	 */
	createOrderWithAdvertiser(advertiserId: string, advertiserName: string): void {
		const state = new OrderCreationNavigationState();
		state.orderOwner.advertiserId = advertiserId;
		state.orderOwner.advertiserName = advertiserName;

		// find this advertiser in the agency-clients records
		let agencyId: string | undefined;
		for (const record of this.agencyClientsRecords) {
			if (record.clients && record.clients.some(client => client.id === advertiserId)) {
				agencyId = record.agencyId;
				break;
			}
		}

		if (agencyId) {
			state.orderOwner.otherPayerId = agencyId;
			state.orderOwner.otherPayerName = this.getProfileCustomerNameById(agencyId);
		}

		this._router.navigate([AppPath.Order], {
			state: state
		});
	}

	/**
	 * Handles the case when the order creation is initiated from the customer card for an agency.
	 * Triggers the creation flow at the select advertiser step.
	 * @param id
	 */
	createOrderWithAgency(agency: Customer): void {
		// Due to the logic in actOnCustomerSelected, we need to determine which role to use depending on the profile scenario.
		// TODO maybe refactor so that this switch is not required.
		if (this._scenario === Scenario.Scenario3_multiAgencies) {
			this.actOnCustomerSelected(agency, OrderCreationCustomerRole.Agency);
		}
		else {
			this.actOnCustomerSelected(agency, OrderCreationCustomerRole.Customer);
		}
	}

	/**
	 * Retrieves the profile customer name by id.
	 * Note: the method assumes the profile is loaded so call it only after the profile$ is resolved with a non-empty value
	 * @param id
	 * @returns
	 */
	private getProfileCustomerNameById(id: string | undefined): string | undefined {
		let res: string | undefined = undefined;

		if (id && this.profile && this.profile.customers) {
			const otherPayer = this.profile.customers.find(x => x.id === id);
			if (otherPayer) {
				res = otherPayer.name;
			}
		}

		return res;
	}
}
